// react
import { useState, type KeyboardEvent } from 'react';

// mui
import { MenuItem, Stack, TextField } from '@mui/material';

// utils
import { isValidEmail } from '@karehero/models';

export interface RelationshipOption {
  value: string;
  label: string;
}

export interface CareCircleInviteDraft {
  /** Left out when the invite doesn't ask for a name. */
  firstName?: string;
  email: string;
  careRecipientAccountRelationshipId: string;
}

type Field = keyof CareCircleInviteDraft;

const isEmailValid = (draft: CareCircleInviteDraft) =>
  isValidEmail(draft.email.trim());

/** Whether every field the draft asks for is filled in. */
export const isInviteDraftComplete = (draft: CareCircleInviteDraft) =>
  draft.firstName?.trim() !== '' &&
  isEmailValid(draft) &&
  draft.careRecipientAccountRelationshipId.length > 0;

export interface CareCircleInviteFieldsProps {
  value: CareCircleInviteDraft;
  onChange: (value: CareCircleInviteDraft) => void;
  relationshipOptions: RelationshipOption[];
  isDisabled?: boolean;
  /** Shows every field's error, as after a submit. */
  isEveryErrorShown?: boolean;
  /** Enter in a text field, for forms whose button sits outside them. */
  onEnter?: () => void;
}

/** CareCircleInviteFields asks who to invite to a care circle. */
export const CareCircleInviteFields = ({
  value,
  onChange,
  relationshipOptions,
  isDisabled = false,
  isEveryErrorShown = false,
  onEnter,
}: CareCircleInviteFieldsProps) => {
  const [blurred, setBlurred] = useState<Partial<Record<Field, boolean>>>({});
  const isShown = (field: Field) => isEveryErrorShown || !!blurred[field];
  const blur = (field: Field) => () =>
    setBlurred((prev) => ({ ...prev, [field]: true }));
  const set = (field: Field) => (text: string) =>
    onChange({ ...value, [field]: text });
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') onEnter?.();
  };

  return (
    <Stack spacing={2}>
      {value.firstName !== undefined && (
        <TextField
          label='First name'
          value={value.firstName}
          onChange={(e) => set('firstName')(e.target.value)}
          onBlur={blur('firstName')}
          onKeyDown={handleKeyDown}
          required
          fullWidth
          autoComplete='given-name'
          error={isShown('firstName') && !value.firstName.trim()}
          disabled={isDisabled}
        />
      )}
      <TextField
        label='Email'
        type='email'
        value={value.email}
        onChange={(e) => set('email')(e.target.value)}
        onBlur={blur('email')}
        onKeyDown={handleKeyDown}
        required
        fullWidth
        autoComplete='email'
        error={isShown('email') && !isEmailValid(value)}
        helperText={
          isShown('email') && value.email.trim() && !isEmailValid(value)
            ? 'Please enter a valid email address'
            : undefined
        }
        disabled={isDisabled}
      />
      <TextField
        label='Relationship to Care Recipient'
        select
        value={value.careRecipientAccountRelationshipId}
        onChange={(e) =>
          set('careRecipientAccountRelationshipId')(e.target.value)
        }
        onBlur={blur('careRecipientAccountRelationshipId')}
        required
        fullWidth
        error={
          isShown('careRecipientAccountRelationshipId') &&
          !value.careRecipientAccountRelationshipId
        }
        disabled={isDisabled}
      >
        {relationshipOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

CareCircleInviteFields.displayName = 'CareCircleInviteFields';
export default CareCircleInviteFields;
